import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  api,
  type ARBucketResponse,
  type ARBucketRow,
  type ARFailureCategory,
  type ARCardHealth,
} from '../api/client'
import { fmtInt } from '../lib/format'
import {
  Hint, StatusBadge, RetryPathBadge, FailureCategoryBadge, CardHealthBadge,
} from '../components/ARGlossary'

const BUCKET_TITLE: Record<string, string> = {
  renewed: 'Renewed',
  pending: 'Charge pending',
  in_retry: 'In retry',
  terminal_fail: 'Terminal fail',
  awaiting: 'Awaiting',
  ar_disabled: 'AR disabled',
}

const FAILURE_OPTIONS: Array<{ v: ARFailureCategory; label: string }> = [
  { v: 'invalid_card_closed', label: 'Invalid card (closed/replaced)' },
  { v: 'invalid_card_expired', label: 'Card expired' },
  { v: 'no_payment_profile', label: 'No card on file' },
  { v: 'declined', label: 'Declined / fraud' },
  { v: 'insufficient_funds', label: 'Insufficient funds' },
  { v: 'avs_mismatch', label: 'AVS mismatch' },
  { v: 'card_type_unsupported', label: 'Card type unsupported' },
  { v: 'system_error', label: 'System error' },
  { v: 'other', label: 'Other' },
]

function monthLabel(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z')
  return d.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

export function ARReportBucketPage() {
  const { month, publication, bucket } = useParams<{ month: string; publication: string; bucket: string }>()
  const cohortMonth = month ? `${month}-01` : ''
  const [filterCategory, setFilterCategory] = useState<ARFailureCategory | ''>('')
  const [filterHealth, setFilterHealth] = useState<ARCardHealth | ''>('')
  const [filterPath, setFilterPath] = useState<'fast' | 'standard' | ''>('')

  const { data, isLoading } = useQuery({
    queryKey: ['ar-bucket', cohortMonth, publication, bucket, filterCategory, filterHealth, filterPath],
    queryFn: () => {
      const params = new URLSearchParams({ limit: '500' })
      if (filterCategory) params.set('failure_category', filterCategory)
      if (filterHealth) params.set('card_health', filterHealth)
      if (filterPath) params.set('retry_path', filterPath)
      return api.get<ARBucketResponse>(
        `/api/ar-report/cohort/${cohortMonth}/${publication}/bucket/${bucket}?${params.toString()}`,
      )
    },
    enabled: !!cohortMonth && !!publication && !!bucket,
  })

  if (isLoading) return <div className="p-8 label text-paper-trace">Loading…</div>
  if (!data) return <div className="p-8 label text-paper-trace">Bucket not found.</div>

  const bucketTitle = BUCKET_TITLE[data.bucket] || data.bucket

  return (
    <div className="p-8 space-y-6 max-w-[1400px]">
      <div className="flex items-center gap-3 flex-wrap">
        <Link to="/ar-report" className="label-amber hover:underline">← AR Report</Link>
        <span className="label text-paper-trace">/</span>
        <Link to={`/ar-report/${month}/${publication}`} className="label-amber hover:underline">
          {monthLabel(data.cohort_month)} · {publication}
        </Link>
        <span className="label text-paper-trace">/</span>
        <span className="label">{bucketTitle}</span>
      </div>

      <header className="flex items-end justify-between border-b border-ink-500 pb-6">
        <div>
          <div className="label mb-1">{bucketTitle} subscribers</div>
          <h1 className="font-serif text-[32px] leading-tight text-paper">
            {fmtInt(data.total_in_bucket)}
            <span className="text-paper-trace text-[18px] font-mono ml-3">
              in this bucket{data.returned < data.total_in_bucket ? ` · showing ${data.returned}` : ''}
            </span>
          </h1>
        </div>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <div className="label mb-1">
            <Hint title="Failure category" short="Why the card failed.">Failure category</Hint>
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value as ARFailureCategory | '')}
            className="bg-ink-900 border border-ink-500 px-3 py-2 text-paper font-mono text-[12px] min-w-[220px]"
          >
            <option value="">All</option>
            {FAILURE_OPTIONS.map((c) => (
              <option key={c.v} value={c.v}>{c.label}</option>
            ))}
          </select>
        </div>
        <div>
          <div className="label mb-1">
            <Hint title="Card health" short="Per-subscriber card-on-file reliability.">Card health</Hint>
          </div>
          <select
            value={filterHealth}
            onChange={(e) => setFilterHealth(e.target.value as ARCardHealth | '')}
            className="bg-ink-900 border border-ink-500 px-3 py-2 text-paper font-mono text-[12px] min-w-[180px]"
          >
            <option value="">All</option>
            <option value="multi_cycle_failure">Multi-cycle (worst)</option>
            <option value="chronic_failure">Chronic</option>
            <option value="transient_decline">Transient</option>
            <option value="healthy">Healthy</option>
          </select>
        </div>
        <div>
          <div className="label mb-1">
            <Hint
              title="Retry path"
              short="Which retry cadence the sub took at attempt 1."
              detail="Fast = invalid_card_closed (gets +22h bonus retry). Standard = everything else (gets +5/+3/+8/+6 schedule only)."
            >
              Retry path
            </Hint>
          </div>
          <select
            value={filterPath}
            onChange={(e) => setFilterPath(e.target.value as 'fast' | 'standard' | '')}
            className="bg-ink-900 border border-ink-500 px-3 py-2 text-paper font-mono text-[12px] min-w-[180px]"
          >
            <option value="">All paths</option>
            <option value="fast">⚡ Fast-retry only</option>
            <option value="standard">Standard cadence only</option>
          </select>
        </div>
        {(filterCategory || filterHealth || filterPath) && (
          <button
            type="button"
            onClick={() => { setFilterCategory(''); setFilterHealth(''); setFilterPath('') }}
            className="label-amber hover:underline pb-2"
          >
            Clear
          </button>
        )}
      </div>

      {/* Subs table */}
      <div className="border border-ink-500 bg-ink-800/40 overflow-x-auto">
        <table className="w-full text-[13px] font-mono">
          <thead>
            <tr className="border-b border-ink-500/60 bg-ink-800/60">
              <th className="label py-2 px-3 font-normal text-left">Email</th>
              <th className="label py-2 px-3 font-normal text-left">Name</th>
              <th className="label py-2 px-3 font-normal text-left">Account #</th>
              <th className="label py-2 px-3 font-normal text-right">
                <Hint title="Attempts count" short="How many AR attempts SC has made on this sub.">Attempts</Hint>
              </th>
              <th className="label py-2 px-3 font-normal text-left">
                <Hint title="Latest status" short="The status from the most recent attempt.">Latest</Hint>
              </th>
              <th className="label py-2 px-3 font-normal text-left">Path</th>
              <th className="label py-2 px-3 font-normal text-left">Reason</th>
              <th className="label py-2 px-3 font-normal text-left">
                <Hint title="Card health" short="Per-subscriber card-on-file reliability across cycles.">Card health</Hint>
              </th>
              <th className="label py-2 px-3 font-normal text-right">
                <Hint title="Days in state" short="Days since this sub first hit a failure status (rough age of their problem).">Days</Hint>
              </th>
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-6 text-paper-trace label">No subscribers match the selected filters.</td></tr>
            ) : (
              data.items.map((row) => <BucketRow key={row.subscription_id} row={row} />)
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function BucketRow({ row }: { row: ARBucketRow }) {
  const subLink = row.subscriber_id ? `/subscribers/${row.subscriber_id}` : null
  return (
    <tr className="border-b border-ink-500/30 hover:bg-ink-700/30">
      <td className="px-3 py-2">
        {subLink ? (
          <Link to={subLink} className="text-paper hover:text-amber-glow">
            {row.email || <span className="text-paper-trace italic">(no email)</span>}
          </Link>
        ) : (
          <span className="text-paper-dim">{row.email || '—'}</span>
        )}
      </td>
      <td className="px-3 py-2 text-paper-dim">
        {[row.first_name, row.last_name].filter(Boolean).join(' ') || '—'}
      </td>
      <td className="px-3 py-2 text-paper-dim">{row.simplecirc_account_id || '—'}</td>
      <td className="px-3 py-2 text-right tabular-nums text-paper">{fmtInt(row.attempts_count)}</td>
      <td className="px-3 py-2"><StatusBadge status={row.latest_status} /></td>
      <td className="px-3 py-2"><RetryPathBadge path={row.first_attempt_retry_path} /></td>
      <td className="px-3 py-2"><FailureCategoryBadge category={row.latest_failure_category} /></td>
      <td className="px-3 py-2"><CardHealthBadge health={row.card_health} /></td>
      <td className="px-3 py-2 text-right tabular-nums text-paper-dim">{fmtInt(row.days_in_state)}</td>
    </tr>
  )
}
